import React from "react";

import { Alert } from "~/components/alert";
import { SkeletonList } from "~/components/skeleton/list";
import { filter } from "~/tools/filter";
import { piped } from "~/tools/piped";
import { prop } from "~/tools/prop";
import { sortBy } from "~/tools/sort-by";

import type { DashboardWidgetSpecInitConfig } from "../../common/dashboard-spec";
import { queryAllFullDashboards } from "../../queries/dashboards";
import { useQuery } from "../../queries/react-query";
import { useDashboardsPermissions } from "../../routes/dashboards/permissions";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";

import { ExistingDashboardEntry } from "./_existing-dashboard-entry";
import { NewDashboardEntry } from "./_new-dashboard-entry";

export interface AddToDashboardContentProps {
	widgets: ReadonlyArray<DashboardWidgetSpecInitConfig>;
	close: VoidFunction;
}

export const AddToDashboardContent: React.FC<AddToDashboardContentProps> = ({ widgets, close }) => {
	const { hasCreateDashboardsPermission, hasUpdateDashboardsPermission } = useDashboardsPermissions().getOrElse({
		hasCreateDashboardsPermission: false,
		hasUpdateDashboardsPermission: () => false,
	});

	const {
		data: dashboards,
		error,
		refetch,
	} = useQuery({
		...queryAllFullDashboards,
		select: piped(filter(hasUpdateDashboardsPermission), sortBy([prop("lastUpdateTimestamp"), "desc"])),
	});

	if (error != null) {
		return <GenericErrorMessage error={error} onRetry={() => void refetch()} />;
	}

	const hasDashboards = dashboards?.length !== 0;

	return (
		<div className="flex flex-col gap-6">
			{hasCreateDashboardsPermission && (
				<section className="flex flex-col gap-3">
					<Heading>New dashboard</Heading>

					<NewDashboardEntry close={close} interactive={dashboards != null} widgets={widgets} />
				</section>
			)}

			{hasDashboards && (
				<section className="flex flex-col gap-3">
					<Heading>Last used dashboards</Heading>

					<SkeletonList className="flex flex-col gap-[3px]" count={5} each={dashboards} el="div">
						{(dashboard) => (
							<ExistingDashboardEntry close={close} dashboard={dashboard} key={dashboard?.id} widgets={widgets} />
						)}
					</SkeletonList>
				</section>
			)}

			{!(hasCreateDashboardsPermission || hasDashboards) && (
				<Alert>Unfortunately, there are no available dashboards you can add this metric to.</Alert>
			)}
		</div>
	);
};

const Heading: React.FC<React.PropsWithChildren> = (props) => (
	<h3 {...props} className="font-medium text-15/5.5 text-grey-900" />
);
